import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '../baseQuery';

export const authApi = createApi({
	reducerPath: 'authApi',
	baseQuery: axiosBaseQuery,
	endpoints: (builder) => ({
		// ── Student Auth ─
		validateClassCode: builder.mutation<any, { classCode: string }>({
			query: (body) => ({
				url: '/auth/student/class-code',
				method: 'POST',
				data: body,
			}),
		}),
		verifyIdentity: builder.mutation<any, Record<string, unknown>>({
			query: (body) => ({
				url: '/auth/student/verify-identity',
				method: 'POST',
				data: body,
			}),
		}),
		studentSetPin: builder.mutation<any, Record<string, unknown>>({
			query: (body) => ({
				url: '/auth/student/set-pin',
				method: 'POST',
				data: body,
			}),
		}),
		studentLogin: builder.mutation<any, Record<string, unknown>>({
			query: (body) => ({
				url: '/auth/student/login',
				method: 'POST',
				data: body,
			}),
		}),

		// ── Parent Auth ─
		parentLogin: builder.mutation<any, Record<string, unknown>>({
			query: (body) => ({
				url: '/auth/parent/login',
				method: 'POST',
				data: body,
			}),
		}),
		parentChangePassword: builder.mutation<any, Record<string, unknown>>({
			query: (body) => ({
				url: '/auth/parent/change-password',
				method: 'PUT',
				data: body,
			}),
		}),

		// ── Trainer Auth ─
		trainerLogin: builder.mutation<any, Record<string, unknown>>({
			query: (body) => ({
				url: '/auth/trainer/login',
				method: 'POST',
				data: body,
			}),
		}),

		// ── School Admin Auth ─
		schoolAdminLogin: builder.mutation<any, Record<string, unknown>>({
			query: (body) => ({
				url: '/auth/school-admin/login',
				method: 'POST',
				data: body,
			}),
		}),
		schoolAdminChangePassword: builder.mutation<any, Record<string, unknown>>({
			query: (body) => ({
				url: '/auth/school-admin/change-password',
				method: 'PUT',
				data: body,
			}),
		}),

		// ── Zonal Manager Auth ─
		zonalManagerLogin: builder.mutation<any, Record<string, unknown>>({
			query: (body) => ({
				url: '/auth/zonal-manager/login',
				method: 'POST',
				data: body,
			}),
		}),
		zonalManagerChangePassword: builder.mutation<any, Record<string, unknown>>({
			query: (body) => ({
				url: '/auth/zonal-manager/change-password',
				method: 'PUT',
				data: body,
			}),
		}),

		// ── Sales Auth ─
		// Unified login — one screen; server detects the sales role.
		salesLogin: builder.mutation<any, Record<string, unknown>>({
			query: (body) => ({
				url: '/auth/sales/login',
				method: 'POST',
				data: body,
			}),
		}),
		salespersonLogin: builder.mutation<any, Record<string, unknown>>({
			query: (body) => ({
				url: '/auth/salesperson/login',
				method: 'POST',
				data: body,
			}),
		}),
		salesManagerLogin: builder.mutation<any, Record<string, unknown>>({
			query: (body) => ({
				url: '/auth/sales-manager/login',
				method: 'POST',
				data: body,
			}),
		}),
		salespersonChangePassword: builder.mutation<any, Record<string, unknown>>({
			query: (body) => ({
				url: '/auth/salesperson/change-password',
				method: 'PUT',
				data: body,
			}),
		}),
		coordinatorChangePassword: builder.mutation<any, Record<string, unknown>>({
			query: (body) => ({
				url: '/auth/appointment-coordinator/change-password',
				method: 'PUT',
				data: body,
			}),
		}),
		salesManagerChangePassword: builder.mutation<any, Record<string, unknown>>({
			query: (body) => ({
				url: '/auth/sales-manager/change-password',
				method: 'PUT',
				data: body,
			}),
		}),

		// ── Master Admin Auth ─
		masterAdminStep1: builder.mutation<any, Record<string, unknown>>({
			query: (body) => ({
				url: '/auth/master-admin/login/step1',
				method: 'POST',
				data: body,
			}),
		}),
		masterAdminStep2: builder.mutation<any, Record<string, unknown>>({
			query: (body) => ({
				url: '/auth/master-admin/login/step2',
				method: 'POST',
				data: body,
			}),
		}),
		setupTotp: builder.mutation<any, void>({
			query: () => ({ url: '/auth/master-admin/setup-totp', method: 'POST' }),
		}),

		// ── Public lookups for face ID ─
		listPublicSchools: builder.query<
			{ id: string; name: string; city: string }[],
			void
		>({
			query: () => ({ url: '/auth/public/schools', method: 'GET' }),
		}),
		listPublicClasses: builder.query<
			{
				id: string;
				classCode: string;
				classNumber: string;
				schoolSection: string;
			}[],
			string
		>({
			query: (schoolId) => ({
				url: `/auth/public/classes/${schoolId}`,
				method: 'GET',
			}),
		}),

		// ── Student Face Auth ─
		studentFaceStatus: builder.query<{ faceEnrolled: boolean }, string>({
			query: (alforaaId) => ({
				url: `/auth/student/face/status?alforaaId=${encodeURIComponent(alforaaId)}`,
				method: 'GET',
			}),
		}),
		studentFaceEnroll: builder.mutation<
			any,
			{ pin: string; faceDescriptor: number[] }
		>({
			query: (body) => ({
				url: '/auth/student/face/enroll',
				method: 'POST',
				data: body,
			}),
		}),
		studentFaceLogin: builder.mutation<
			any,
			{
				alforaaId?: string;
				schoolId?: string;
				classId?: string;
				faceDescriptor: number[];
			}
		>({
			query: (body) => ({
				url: '/auth/student/face/login',
				method: 'POST',
				data: body,
			}),
		}),
		studentFaceRemove: builder.mutation<any, { pin: string }>({
			query: (body) => ({
				url: '/auth/student/face/remove',
				method: 'DELETE',
				data: body,
			}),
		}),
		studentVerifyPin: builder.mutation<{ valid: boolean }, { pin: string }>({
			query: (body) => ({
				url: '/auth/student/face/verify-pin',
				method: 'POST',
				data: body,
			}),
		}),
		dismissFacePrompt: builder.mutation<any, void>({
			query: () => ({
				url: '/auth/student/face/dismiss-prompt',
				method: 'PATCH',
			}),
		}),

		// ── Shared ─
		logout: builder.mutation<any, { refreshToken: string }>({
			query: (body) => ({ url: '/auth/logout', method: 'POST', data: body }),
		}),
	}),
});

export const {
	useValidateClassCodeMutation,
	useVerifyIdentityMutation,
	useStudentSetPinMutation,
	useStudentLoginMutation,
	useParentLoginMutation,
	useParentChangePasswordMutation,
	useTrainerLoginMutation,
	useSchoolAdminLoginMutation,
	useZonalManagerLoginMutation,
	useZonalManagerChangePasswordMutation,
	useSchoolAdminChangePasswordMutation,
	useSalesLoginMutation,
	useSalespersonLoginMutation,
	useSalesManagerLoginMutation,
	useSalespersonChangePasswordMutation,
	useCoordinatorChangePasswordMutation,
	useSalesManagerChangePasswordMutation,
	useMasterAdminStep1Mutation,
	useMasterAdminStep2Mutation,
	useSetupTotpMutation,
	useListPublicSchoolsQuery,
	useLazyListPublicSchoolsQuery,
	useListPublicClassesQuery,
	useLazyListPublicClassesQuery,
	useStudentFaceStatusQuery,
	useLazyStudentFaceStatusQuery,
	useStudentFaceEnrollMutation,
	useStudentFaceLoginMutation,
	useStudentFaceRemoveMutation,
	useStudentVerifyPinMutation,
	useDismissFacePromptMutation,
	useLogoutMutation,
} = authApi;
